import { useGetClubRegistrationByIdQuery } from "@/services/ClubRegistrationService";
import { KeenIcon } from "@/components";
import CircularProgress from "@mui/material/CircularProgress";
import clsx from "clsx";
import { useRef, useEffect } from "react";

export const RegistrationViewModal = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) => {
  const { data, isLoading } = useGetClubRegistrationByIdQuery(id);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management
  useEffect(() => {
    let attempts = 0;
    let timer: NodeJS.Timeout;

    const tryFocus = () => {
      if (closeButtonRef.current && attempts < 10) {
        closeButtonRef.current.focus();
        // Check if focus was successful
        if (document.activeElement === closeButtonRef.current) {
          return; // Success, stop trying
        }
        attempts++;
        timer = setTimeout(tryFocus, 100);
      }
    };

    // Start trying to focus after a short delay
    timer = setTimeout(tryFocus, 100);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <CircularProgress aria-label="Loading registration details" />
        <p className="mt-2">Loading registration details...</p>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-2xl max-h-[95dvh] overflow-y-auto bg-gray-100 rounded-2xl p-6 shadow-xl mx-auto">
      <button
        ref={closeButtonRef}
        type="button"
        onClick={onClose}
        data-testid="modal-close"
        aria-label="Close modal"
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 focus:outline-none"
      >
        <KeenIcon type="filled" icon="close" className="h-6 w-6" />
      </button>

      <h1 className="text-lg font-medium leading-6 text-gray-900 border-b pb-4">
        Registration Details
      </h1>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="form-control">
          <label htmlFor="name" className="label">
            <span className="label-text">Name</span>
          </label>
          <input
            id="name"
            className={clsx("input input-bordered w-full")}
            value={data?.name || "-"}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="location" className="label">
            <span className="label-text">Location</span>
          </label>
          <input
            id="location"
            className={clsx("input input-bordered w-full")}
            value={data?.location || "-"}
            readOnly
          />
        </div>
        <div className="form-control sm:col-span-2">
          <label htmlFor="description" className="label">
            <span className="label-text">Description</span>
          </label>
          <textarea
            id="description"
            className={clsx("textarea textarea-bordered w-full")}
            value={data?.description || "-"}
            rows={3}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="website" className="label">
            <span className="label-text">Website</span>
          </label>
          <input
            id="website"
            className={clsx("input input-bordered w-full")}
            value={data?.website || "-"}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="contact_email" className="label">
            <span className="label-text">Contact Email</span>
          </label>
          <input
            id="contact_email"
            className={clsx("input input-bordered w-full")}
            value={data?.contact_email || "-"}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="street_address" className="label">
            <span className="label-text">Street Address</span>
          </label>
          <input
            id="street_address"
            className={clsx("input input-bordered w-full")}
            value={data?.street_address || "-"}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="postal_code" className="label">
            <span className="label-text">Postal Code</span>
          </label>
          <input
            id="postal_code"
            className={clsx("input input-bordered w-full")}
            value={data?.postal_code || "-"}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="city" className="label">
            <span className="label-text">City</span>
          </label>
          <input
            id="city"
            className={clsx("input input-bordered w-full")}
            value={data?.city || "-"}
            readOnly
          />
        </div>
        <div className="form-control">
          <label htmlFor="business_id" className="label">
            <span className="label-text">Business ID</span>
          </label>
          <input
            id="business_id"
            className={clsx("input input-bordered w-full")}
            value={data?.business_id || "-"}
            readOnly
          />
        </div>
      </div>
    </div>
  );
};
